'use client';

import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import { getResponsiveAttributes, spacingMap } from '../utils';
import type {
  PolymorphicForwardRefComponentV2,
  ResponsiveValue,
} from '../utils/types';
import styles from './stack.component.module.scss';

type LegacySpacing = 0 | 4 | 8 | 10 | 12 | 16 | 20 | 24 | 32 | 40;
type StackSpacing =
  | 0
  | 'x3s'
  | 'xxs'
  | 'xs'
  | 's'
  | 'm'
  | 'l'
  | 'xl'
  | 'xxl'
  | 'x3l'
  | 'x4l'
  | 'x5l';
type Spacing = LegacySpacing | StackSpacing;
type ResponsiveSpacing = ResponsiveValue<Spacing>;
type Direction = 'row' | 'column';
type ResponsiveDirection = ResponsiveValue<Direction>;
type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';
type ResponsiveFlexWrap = ResponsiveValue<FlexWrap>;
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type ResponsiveAlign = ResponsiveValue<Align>;

type StackOwnProps = {
  children: ReactNode;
  /**
   * Stack内の要素間の間隔
   * 数値キーワード(0, 4 ...)は非推奨です。
   * x3s, xxs 等のキーワードを使用してください。
   * @link https://findy.kibe.la/notes/35829#semantictoken
   */
  spacing?: Spacing | ResponsiveSpacing;
  /**
   * Stackの主軸に沿った子要素の配置方法
   */
  justify?:
    | 'start'
    | 'center'
    | 'end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch';
  /**
   * Stackの交差軸に沿った子要素の配置方法
   */
  align?: Align | ResponsiveAlign;
  /**
   * Stackの方向
   */
  direction?: Direction | ResponsiveDirection;
  /**
   * Stackの折り返し方法
   */
  wrap?: FlexWrap | ResponsiveFlexWrap;
};

// TODO: デザインシステム側で汎用スペーシングの整理が完了したら整理する
const deprecatedStackSpacingMap = spacingMap;
const stackSpacingMap = Object.assign(deprecatedStackSpacingMap, {
  x3s: 'x3s',
  xxs: 'xxs',
  xs: 'xs',
  s: 's',
  m: 'm',
  l: 'l',
  xl: 'xl',
  xxl: 'xxl',
  x3l: 'x3l',
  x4l: 'x4l',
  x5l: 'x5l',
} as const);

export const Stack = forwardRef(
  (
    {
      as = 'div',
      children,
      spacing = 16,
      justify = 'start',
      align = 'stretch',
      // TODO: スタイルの実装が完了したらデフォルト値を有効化する
      direction,
      wrap,
      className,
      ...rest
    },
    forwardedRef
  ) => {
    const Root = as;
    return (
      <Root
        className={[className, styles.stack].filter(Boolean).join(' ')}
        /* @ts-expect-error polymorphic componentとして扱うためにHTMLDivElementとHTMLSectionElementの型エラーを許容する */
        ref={forwardedRef}
        {...getResponsiveAttributes('gap', spacing, stackSpacingMap)}
        {...getResponsiveAttributes('flex-direction', direction)}
        {...getResponsiveAttributes('flex-wrap', wrap)}
        {...getResponsiveAttributes('align-items', align)}
        data-justify={justify}
        {...rest}
      >
        {children}
      </Root>
    );
  }
) as PolymorphicForwardRefComponentV2<
  'div',
  'div' | 'section' | 'p',
  StackOwnProps
>;

Stack.displayName = 'Stack';
